import { ISmplPose, ISmplPoseDto, SmplPoseDto } from '@seddi/avatar';

import { getAuthToken } from '../auth';
import { VTO_AVATARS_ADDRESS } from '../constants';
import { Avatar, SmplAvatar, StarAvatar } from './avatarTypes';

type ISmplPoseDtoEx = ISmplPoseDto & {
  priority: number;
};

/**
 * Get avatar from ID.
 * @param id The avatar ID.
 */
export const fetchAvatar = async (id: string) => {
  const idToken = await getAuthToken();
  const headers = {
    ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    'Content-Type': 'application/json',
  };

  const response = await fetch(`${VTO_AVATARS_ADDRESS}/v1/avatars/${id}`, {
    headers,
  });

  const { status } = response;
  if (status < 200 || status >= 400) {
    throw new Error();
  }

  const dto = (await response.json()) as Avatar;
  return dto;
};

/**
 * Get pose from ID.
 * @param id The pose ID.
 */
export const fetchPose = async (id: string): Promise<ISmplPose> => {
  const idToken = await getAuthToken();
  const headers = {
    ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    'Content-Type': 'application/json',
  };

  const response = await fetch(`${VTO_AVATARS_ADDRESS}/v1/poses/${id}`, {
    headers,
  });

  const { status } = response;
  if (status < 200 || status >= 400) {
    throw new Error();
  }

  const dto = await response.json();
  const domain = SmplPoseDto.ToDomain(dto);

  return domain;
};

/**
 * Get all avatar poses.
 * @returns The poses array.
 */
export const fetchPoses = async (): Promise<ISmplPose[]> => {
  const idToken = await getAuthToken();
  const headers: Record<string, string> = {
    ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    'Content-Type': 'application/json',
  };

  const response = await fetch(`${VTO_AVATARS_ADDRESS}/v1/poses/list`, {
    headers,
  });

  const { status } = response;
  if (status < 200 || status >= 400) {
    throw new Error();
  }

  const dtos = (await response.json()) as ISmplPoseDtoEx[];
  // sort descending priority
  dtos.sort((a, b) => b.priority - a.priority);
  const domains = dtos.map(dto => SmplPoseDto.ToDomain(dto));

  return domains;
};

/**
 * Create anonymous avatar from SMPL shape.
 * @param shape SMPL shape.
 */
export const fetchCreateAnonymousAvatarFromSmpl = async (shape: SmplAvatar): Promise<Avatar> => {
  const idToken = await getAuthToken();
  const headers = {
    ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    'Content-Type': 'application/json',
  };

  const response = await fetch(`${VTO_AVATARS_ADDRESS}/v1/anonymous-avatars/from-smpl`, {
    headers,
    method: 'PUT',
    body: JSON.stringify(shape),
  });

  const { status } = response;
  if (status < 200 || status >= 400) {
    throw new Error();
  }

  const avatar = (await response.json()) as Avatar;

  return avatar;
};

/**
 * Create anonymous avatar from STAR.
 * @returns The anonymous avatar.
 */
export const fetchCreateAnonymousAvatarFromStar = async (shape: StarAvatar): Promise<Avatar> => {
  const idToken = await getAuthToken();
  const headers = {
    ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    'Content-Type': 'application/json',
  };

  const response = await fetch(`${VTO_AVATARS_ADDRESS}/v1/anonymous-avatars/from-star`, {
    headers,
    method: 'PUT',
    body: JSON.stringify(shape),
  });

  const { status } = response;
  if (status < 200 || status >= 400) {
    throw new Error();
  }

  const avatar = (await response.json()) as Avatar;

  return avatar;
};
